import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import api from "../../../services/api";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const PAGE_SIZE = 10;

const LiftingList = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Operation dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [eventType, setEventType] = useState("");

  // Lift Up fields
  const [liftPlace, setLiftPlace] = useState("");

  // Lift Down / False Lift fields
  const [trolleyNo1, setTrolleyNo1] = useState("");
  const [trolleyNo2, setTrolleyNo2] = useState("");

  // Common field
  const [remarks, setRemarks] = useState("");

  // Lift history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyCoach, setHistoryCoach] = useState(null);

  const getToken = () => localStorage.getItem("token");

  // Resolve the visit ID from possible queue response field names.
  const getVisitId = (row) => {
    const value = row?.visit_id ?? row?.workshop_visit_id ?? row?.id;

    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }

    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  };

  const getCoachNumber = (row) => row?.coach_no || row?.coach_number || "-";

  const getStatus = (row) => row?.current_status || row?.status || "YARD";

  const getActionLabel = (type) => {
    switch (type) {
      case "LIFT_UP":
        return "Lift Up";
      case "LIFT_DOWN":
        return "Lift Down";
      case "FALSE_LIFT":
        return "False Lift";
      default:
        return "Lifting Operation";
    }
  };

  // Load queue
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/lifting/queue", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: search.trim(),
        },
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const result = response.data;

      

      const list = Array.isArray(result)
        ? result
        : result?.data || result?.rows || result?.items || [];

      setRows(Array.isArray(list) ? list : []);

      const total =
        result?.total ??
        result?.pagination?.total ??
        result?.totalRecords ??
        list.length;

      const pages =
        result?.totalPages ??
        result?.pagination?.totalPages ??
        Math.max(1, Math.ceil(Number(total) / PAGE_SIZE));

      setTotalPages(Math.max(1, Number(pages) || 1));
    } catch (err) {
      console.error("Lifting queue error:", err.response?.data || err);

      setRows([]);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load lifting queue.",
      );
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQueue();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchQueue]);

  // Open operation dialog
  const openActionDialog = (row, type) => {
    setError("");
    setSuccess("");

    setSelectedRow(row);
    setEventType(type);
    setLiftPlace("");
    setTrolleyNo1("");
    setTrolleyNo2("");
    setRemarks("");
    setDialogOpen(true);

    console.log("Selected lifting queue record:", row);
    console.log("Resolved visit ID:", getVisitId(row));
  };

  const closeDialog = () => {
    if (saving) return;

    setDialogOpen(false);
    setSelectedRow(null);
  };

  // Validate and submit operation
  const submitEvent = async () => {
    if (!selectedRow) {
      setError("Please select a coach.");
      return;
    }

    const visitId = getVisitId(selectedRow);

    if (!visitId) {
      console.error("Invalid visit record:", selectedRow);
      setError(
        "Visit ID is missing or invalid. Check the queue response in the browser console.",
      );
      return;
    }

    if (eventType === "LIFT_UP" && !liftPlace.trim()) {
      setError("Please enter the lifting place.");
      return;
    }

    if (
      ["LIFT_DOWN", "FALSE_LIFT"].includes(eventType) &&
      (!trolleyNo1.trim() || !trolleyNo2.trim())
    ) {
      setError("Please enter both trolley numbers.");
      return;
    }

    const payload = {
      event_type: eventType,
      lift_place: eventType === "LIFT_UP" ? liftPlace.trim() : null,
      trolley_no_1: trolleyNo1.trim() || null,
      trolley_no_2: trolleyNo2.trim() || null,
      remarks: remarks.trim(),
    };

    setSaving(true);
    setError("");

    try {
      const response = await api.post(`/lifting/${visitId}/event`, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setSuccess(
        response.data?.message ||
          `${getActionLabel(eventType)} saved successfully.`,
      );

      setDialogOpen(false);
      setSelectedRow(null);

      await fetchQueue();
    } catch (err) {
      console.error("Lifting operation error:", err.response?.data || err);

      setError(
        err.response?.data?.message || "Unable to save lifting operation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    const visitId = getVisitId(row);
    if (!visitId) {
      setError("Visit ID is missing; history cannot be loaded.");
      return;
    }

    setHistoryCoach(row);
    setHistoryRows([]);
    setHistoryError("");
    setHistoryLoading(true);
    setHistoryOpen(true);

    try {
      const response = await api.get(`/lifting/${visitId}/history`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const result = response.data;
      const list = Array.isArray(result)
        ? result
        : result?.data || result?.rows || result?.history || [];

      setHistoryRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Lifting history error:", err.response?.data || err);
      setHistoryError(
        err.response?.data?.message || "Unable to load lifting history."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    if (historyLoading) return;
    setHistoryOpen(false);
    setHistoryCoach(null);
    setHistoryRows([]);
    setHistoryError("");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const getHistoryEventLabel = (event) => {
    const type = event?.event_type || event?.operation || event?.action_type;
    return getActionLabel(type);
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = String(value).slice(0, 10);
    return date === "Invalid " ? "-" : date;
  };

  const getStatusColor = (status) => {
    switch (String(status).toUpperCase()) {
      case "YARD":
        return "warning";
      case "IN_PROGRESS":
        return "info";
      case "COMPLETED":
        return "success";
      default:
        return "default";
    }
  };


  const isCoachLifted = (row) => {
        return (
          row?.is_lifted === 1 ||
          row?.is_lifted === true ||
          row?.is_lifted === "1"
        );
      };
      
  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Coach Lifting
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Yard coaches and lifting operations
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchQueue}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <TextField
              size="small"
              placeholder="Search coach or visit number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{ width: { xs: "100%", sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Chip
              icon={<EngineeringIcon />}
              label={`${rows.length} shown`}
              color="primary"
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Queue table */}
      <Card variant="outlined">
        {loading ? (
          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <InfoOutlinedIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />

            <Typography color="text.secondary">
              No coaches found in the lifting queue.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Mobile coach cards */}
            <Box sx={{ display: { xs: "block", md: "none" }, p: 1 }}>
              <Stack spacing={1.25}>
                {rows.map((row, index) => {
                  const visitId = getVisitId(row);
                  const lifted = isCoachLifted(row);
                  return (
                    <Card key={visitId || `${row.visit_no}-${index}`} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={800} variant="subtitle1">{getCoachNumber(row)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Visit: {row.visit_no || "-"} · {row.coach_type_name || row.coach_type_code || "-"}
                            </Typography>
                          </Box>
                          <Chip size="small" label={getStatus(row)} color={getStatusColor(getStatus(row))} variant="outlined" />
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Stack spacing={0.35}>
                          <Typography variant="body2"><b>Entry:</b> {formatDate(row.gate_in_date)}</Typography>
                          <Typography variant="body2"><b>Lifting place:</b> {lifted ? row.lift_place || "-" : "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">S. No. {(page - 1) * PAGE_SIZE + index + 1}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                          {lifted ? (
                            <>
                              <Button size="small" variant="outlined" color="warning" startIcon={<ArrowDownwardIcon />} onClick={() => openActionDialog(row, "LIFT_DOWN")}>Lift Down</Button>
                              <Button size="small" variant="outlined" color="error" onClick={() => openActionDialog(row, "FALSE_LIFT")}>False Lift</Button>
                            </>
                          ) : (
                            <Button size="small" variant="contained" color="success" startIcon={<ArrowUpwardIcon />} onClick={() => openActionDialog(row, "LIFT_UP")}>Lift Up</Button>
                          )}
                          <Button size="small" variant="text" disabled={!visitId} onClick={() => openHistory(row)}>History</Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>

            {/* Tablet and desktop table */}
            <TableContainer sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    {["#", "Coach No.", "Visit No.", "Coach Type", "Lifting Place", "Entry Date", "Status", "Actions"].map((label) => (
                      <TableCell key={label} align={label === "Actions" ? "center" : "left"}><b>{label}</b></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => {
                    const visitId = getVisitId(row);
                    return (
                      <TableRow key={visitId || `${row.visit_no}-${index}`} hover>
                        <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                        <TableCell><Typography fontWeight={700}>{getCoachNumber(row)}</Typography></TableCell>
                        <TableCell>{row.visit_no || "-"}</TableCell>
                        <TableCell>{row.coach_type_name || row.coach_type_code || "-"}</TableCell>
                        <TableCell>{isCoachLifted(row) ? row.lift_place || "-" : "-"}</TableCell>
                        <TableCell>{formatDate(row.gate_in_date)}</TableCell>
                        <TableCell><Chip size="small" label={getStatus(row)} color={getStatusColor(getStatus(row))} variant="outlined" /></TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                            {isCoachLifted(row) ? (
                              <>
                                <Button size="small" variant="outlined" color="warning" startIcon={<ArrowDownwardIcon />} onClick={() => openActionDialog(row, "LIFT_DOWN")}>Lift Down</Button>
                                <Button size="small" variant="outlined" color="error" onClick={() => openActionDialog(row, "FALSE_LIFT")}>False Lift</Button>
                              </>
                            ) : (
                              <Button size="small" variant="contained" color="success" startIcon={<ArrowUpwardIcon />} onClick={() => openActionDialog(row, "LIFT_UP")}>Lift Up</Button>
                            )}
                            <Button size="small" variant="text" disabled={!visitId} onClick={() => openHistory(row)}>History</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <Stack direction="row" justifyContent="center" sx={{ p: { xs: 1, sm: 2 }, overflowX: "auto" }}>
              <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)}
                color="primary" size="small" siblingCount={0} boundaryCount={1} />
            </Stack>
          </>
        )}
      </Card>

      {/* Operation dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" fullScreen={false} PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: { xs: "calc(100% - 16px)", sm: "100%" }, maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" } } }}>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {getActionLabel(eventType)}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Coach: {selectedRow ? getCoachNumber(selectedRow) : ""}
              </Typography>
            </Box>

            <IconButton onClick={closeDialog} disabled={saving} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {/* Lift Up */}
          {eventType === "LIFT_UP" && (
            <Stack spacing={2}>
              <Alert severity="info">
                Lift Up will also initiate Shell In automatically if your
                backend implements that workflow.
              </Alert>

              <TextField
                fullWidth
                label="Lifting Place"
                placeholder="Enter lifting place"
                value={liftPlace}
                onChange={(e) => setLiftPlace(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                multiline
                minRows={3}
                placeholder="Enter remarks"
              />
            </Stack>
          )}

          {/* Lift Down */}
          {eventType === "LIFT_DOWN" && (
            <Stack spacing={2}>
              <Alert severity="warning">
                Enter both trolley numbers used for this operation.
              </Alert>

              <TextField
                fullWidth
                label="Trolley Number 1"
                value={trolleyNo1}
                onChange={(e) => setTrolleyNo1(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Trolley Number 2"
                value={trolleyNo2}
                onChange={(e) => setTrolleyNo2(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                multiline
                minRows={3}
                placeholder="Enter remarks"
              />
            </Stack>
          )}

          {/* False Lift */}
          {eventType === "FALSE_LIFT" && (
            <Stack spacing={2}>
              <Alert severity="info">
                Record both trolley numbers and the reason for the false lift
                operation.
              </Alert>

              <TextField
                fullWidth
                label="Trolley Number 1"
                value={trolleyNo1}
                onChange={(e) => setTrolleyNo1(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Trolley Number 2"
                value={trolleyNo2}
                onChange={(e) => setTrolleyNo2(e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Remarks / Reason"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                multiline
                minRows={3}
                placeholder="Enter reason or remarks"
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving} color="inherit">
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={submitEvent}
            disabled={saving}
            color={
              eventType === "LIFT_UP"
                ? "success"
                : eventType === "LIFT_DOWN"
                  ? "warning"
                  : "error"
            }
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Operation"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lifting history dialog */}
      <Dialog
        open={historyOpen}
        onClose={closeHistory}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              Lifting History
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Coach: {historyCoach ? getCoachNumber(historyCoach) : "-"}
                {" | "}
                Visit: {historyCoach?.visit_no || "-"}
              </Typography>
            </Box>
            <IconButton
              onClick={closeHistory}
              disabled={historyLoading}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {historyError}
            </Alert>
          )}

          {historyLoading ? (
            <Box
              sx={{
                minHeight: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : historyRows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No lifting history found for this visit.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell><b>#</b></TableCell>
                    <TableCell><b>Date &amp; Time</b></TableCell>
                    <TableCell><b>Operation</b></TableCell>
                    <TableCell><b>Lifting Place</b></TableCell>
                    <TableCell><b>Trolley 1</b></TableCell>
                    <TableCell><b>Trolley 2</b></TableCell>
                    <TableCell><b>Remarks</b></TableCell>
                    <TableCell><b>Operator</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRows.map((event, index) => (
                    <TableRow
                      key={event.id || event.event_id || `${index}-${event.event_type}`}
                      hover
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {formatDateTime(
                          event.event_datetime ||
                          event.event_date ||
                          event.created_at
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={getHistoryEventLabel(event)}
                          color={
                            event.event_type === "LIFT_UP"
                              ? "success"
                              : event.event_type === "FALSE_LIFT"
                                ? "error"
                                : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>{event.lift_place || "-"}</TableCell>
                      <TableCell>
                        {event.trolley_no_1 || event.trolley_no1 || "-"}
                      </TableCell>
                      <TableCell>
                        {event.trolley_no_2 || event.trolley_no2 || "-"}
                      </TableCell>
                      <TableCell>{event.remark || "-"}</TableCell>
                      <TableCell>
                        {event.username ||
                          event.operator_name ||
                          event.createdby ||
                          "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeHistory} disabled={historyLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiftingList;
